import * as React from 'react'
import { useForkRef, useEventCallback, debounce } from '@applift/factor'
import {
  useVirtualizer,
  defaultRangeExtractor,
} from '../../core/useReactVirtual'
import { gridColumnsTotalWidthSelector } from '../columns/gridColumnsSelector'
import { useGridApiContext } from '../../utils/useGridApiContext'
import { useGridPrivateApiContext } from '../../utils/useGridPrivateApiContext'
import { useGridRootProps } from '../../utils/useGridRootProps'
import { gridColumnOffsetSelector } from '../columnWidthOffset/columnWidthOffsetSelector'
import {
  gridDensityColumnFooterHeightSelector,
  gridDensityRowHeightSelector,
} from '../density/densitySelector'
import { useGridSelector } from '../../utils/useGridSelector'
import { useGridSelectorAdvanced } from '../../utils/useGridSelector'
import { gridRowsMetaSelector } from '../rows/gridRowsMetaSelector'
import { GridEventListener } from '../../../models/events'
import { useGridApiEventHandler } from '../../utils/useGridApiEventHandler'
import {
  gridColumnCenterCount,
  gridColumnLeftWidthSelector,
  gridColumnCenterCellSize,
  gridColumnsTotalPinnedWidthSelector,
} from '../columns/gridColumnsSelector'
import { RenderContext } from '../../../models/params/gridScrollParams'
import {
  ReactVirtualItem,
  ReactVirtualRange,
} from '../../../models/gridReactVirtual'
import { GridVirtualizationApi } from '../../../models/api/gridVirtulizationApi'
import { gridFocusCellSelector, gridTabIndexCellSelector } from '../focus'

interface UseGridVirtualScrollerProps {
  ref: React.Ref<HTMLDivElement>
  disableVirtualization?: boolean
  onRenderZonePositioning?: (params: { top: number; left: number }) => void
}

type ScrollDirectionVertical = 'up' | 'down'
type ScrollDirectionHorizontal = 'left' | 'right'

export const useGridVirtualScroller = (props: UseGridVirtualScrollerProps) => {
  const { ref, disableVirtualization, onRenderZonePositioning } = props
  const apiRef = useGridApiContext()
  const privateApiRef = useGridPrivateApiContext()
  const rootProps = useGridRootProps()
  const columnsWidthOffset = useGridSelector(apiRef, gridColumnOffsetSelector)
  const columnCellSizeSelector = gridColumnCenterCellSize(apiRef.current)
  const rowHeight = useGridSelector(apiRef, gridDensityRowHeightSelector)
  let columnFooterHeight = useGridSelector(
    apiRef,
    gridDensityColumnFooterHeightSelector
  )
  if (rootProps.footerPlacement === false) {
    columnFooterHeight = 0
  }
  const tableInstance = apiRef.current.tableInstance
  const rowsMeta = useGridSelectorAdvanced(
    apiRef,
    rootProps,
    gridRowsMetaSelector
  )
  const cellFocus = useGridSelector(apiRef, gridFocusCellSelector)
  const cellTabIndex = useGridSelector(apiRef, gridTabIndexCellSelector)
  const rows = tableInstance.getRowModel().rows
  const columnsTotalWidth = gridColumnsTotalWidthSelector(apiRef.current)
  const centerColumnsCount = gridColumnCenterCount(apiRef.current)
  const totalPinnedWidth = gridColumnsTotalPinnedWidthSelector(apiRef.current)
  const leftTotalWidth = gridColumnLeftWidthSelector(apiRef.current)
  const [hasScrollX, setHasScrollX] = React.useState(false)
  const [hasScrollY, setHasScrollY] = React.useState(false)
  const [scrollbarSize, setScrollbarSize] = React.useState(0)

  const renderZoneRef = React.useRef<HTMLDivElement>(null)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const handleRef = useForkRef(ref, rootRef)
  const [containerWidth, setContainerWidth] = React.useState<number | null>(
    null
  )
  const scrollPosition = React.useRef({ top: 0, left: 0 })
  const scrollDirVertical = React.useRef<ScrollDirectionVertical | null>(null)
  const scrollDirHorizontal = React.useRef<ScrollDirectionHorizontal | null>(
    null
  )
  const prevRowRangeRef = React.useRef<ReactVirtualRange | null>(null)
  const prevColRangeRef = React.useRef<ReactVirtualRange | null>(null)
  const prevCenterColumnCount = React.useRef(centerColumnsCount)
  const prevRowCount = React.useRef(rows.length)
  const totalRows = rootProps.rowCount!
  const pageSize = rootProps.pageSize!
  const loading = rootProps.loading

  const skeletonSize = React.useMemo(
    () =>
      totalRows && rows.length < totalRows
        ? totalRows - rows.length < pageSize
          ? totalRows - rows.length
          : pageSize
        : loading
          ? pageSize
          : 0,
    [totalRows, pageSize, loading, rows.length]
  )

  const virtualRow = useVirtualizer({
    count: rows.length + skeletonSize,
    estimateSize: () => rowHeight,
    getScrollElement: () => (disableVirtualization ? null : rootRef.current),
    overscan: rootProps.rowBuffer,
    scrollPaddingEnd: hasScrollX
      ? columnFooterHeight + scrollbarSize
      : columnFooterHeight,
    rangeExtractor: range => {
      if (prevRowCount.current !== range.count) {
        prevRowCount.current = range.count
        prevRowRangeRef.current = null
      }
      if (prevRowRangeRef.current === null && range.endIndex > 0) {
        prevRowRangeRef.current = range
      }
      if (scrollDirVertical.current === 'down') {
        if (
          prevRowRangeRef.current &&
          range.endIndex <=
            prevRowRangeRef.current.endIndex + rootProps.rowThreshold
        ) {
          return defaultRangeExtractor(prevRowRangeRef.current)
        } else {
          prevRowRangeRef.current = range
          return defaultRangeExtractor(prevRowRangeRef.current)
        }
      } else if (scrollDirVertical.current === 'up') {
        if (
          prevRowRangeRef.current &&
          range.startIndex >=
            prevRowRangeRef.current.startIndex - rootProps.rowThreshold
        ) {
          return defaultRangeExtractor(prevRowRangeRef.current)
        } else {
          prevRowRangeRef.current = range
          return defaultRangeExtractor(prevRowRangeRef.current)
        }
      } else {
        return defaultRangeExtractor(range)
      }
    },
  })

  const skeletonVisible = React.useMemo(
    () =>
      (virtualRow.range?.endIndex ?? 0) >=
      (rows.length > 0 ? rows.length - 1 : rows.length),
    [virtualRow.range?.endIndex, rows.length]
  )

  const onFetchRows = rootProps.onFetchRows

  React.useEffect(() => {
    if (
      skeletonVisible &&
      skeletonSize &&
      loading === false &&
      typeof onFetchRows === 'function'
    ) {
      const sortModel = tableInstance.getState().sorting
      const filterModel = tableInstance.getState().columnFilters
      onFetchRows({
        startIndex: (virtualRow.range?.endIndex ?? 0) + 1,
        endIndex: (virtualRow.range?.endIndex ?? 0) + skeletonSize,
        pageSize,
        sortModel,
        filterModel,
      })
    }
  }, [
    onFetchRows,
    virtualRow,
    skeletonVisible,
    skeletonSize,
    pageSize,
    loading,
    tableInstance,
  ])

  const virtualColumn = useVirtualizer({
    horizontal: true,
    count: centerColumnsCount,
    estimateSize: index => columnCellSizeSelector(index),
    getScrollElement: () => (disableVirtualization ? null : rootRef.current),
    overscan: rootProps.columnBuffer,
    scrollPaddingEnd: hasScrollY
      ? totalPinnedWidth + scrollbarSize
      : totalPinnedWidth,
    rangeExtractor: range => {
      if (prevCenterColumnCount.current !== centerColumnsCount) {
        prevCenterColumnCount.current = centerColumnsCount
        prevColRangeRef.current = null
      }
      if (prevColRangeRef.current === null && range.endIndex > 0) {
        prevColRangeRef.current = range
      }
      if (scrollDirHorizontal.current === 'right') {
        if (
          prevColRangeRef.current &&
          range.endIndex <=
            prevColRangeRef.current.endIndex + rootProps.columnThreshold
        ) {
          return defaultRangeExtractor(prevColRangeRef.current)
        } else {
          prevColRangeRef.current = range
          return defaultRangeExtractor(prevColRangeRef.current)
        }
      } else if (scrollDirHorizontal.current === 'left') {
        if (
          prevColRangeRef.current &&
          range.startIndex >=
            prevColRangeRef.current.startIndex - rootProps.columnThreshold
        ) {
          return defaultRangeExtractor(prevColRangeRef.current)
        } else {
          prevColRangeRef.current = range
          return defaultRangeExtractor(prevColRangeRef.current)
        }
      } else {
        return defaultRangeExtractor(range)
      }
    },
  })

  const constRowRange = React.useMemo(() => {
    if (!disableVirtualization) {
      return [] as ReactVirtualItem[]
    }
    return computeFixedRanges(rows.length, rowHeight)
  }, [disableVirtualization, rows.length, rowHeight])

  const constColRange = React.useMemo(() => {
    if (!disableVirtualization) {
      return [] as ReactVirtualItem[]
    }
    return computeFixedRanges(centerColumnsCount, columnCellSizeSelector)
  }, [disableVirtualization, centerColumnsCount, columnCellSizeSelector])

  const rowRanges = disableVirtualization
    ? constRowRange
    : virtualRow.getVirtualItems()
  const colRanges = disableVirtualization
    ? constColRange
    : virtualColumn.getVirtualItems()

  const renderContext: RenderContext = {
    colRanges: colRanges,
    rowRanges: rowRanges,
    columnOffset:
      // @ts-ignore
      leftTotalWidth + (columnsWidthOffset?.[colRanges?.[0]?.index] ?? 0),
    rowOffset: (rowRanges[0]?.index ?? 0) * rowHeight,
    // Todo: Need to subtract total top-pinned rows height from totalHeight
    totalHeight: rowsMeta.currentPageTotalHeight,
    rows,
    rowHeight,
    columnFooterHeight,
    containerWidth: containerWidth ?? 0,
    skeletonSize: skeletonVisible ? skeletonSize : 0,
    cellFocus: cellFocus,
    cellTabIndex: cellTabIndex,
  }
  if (renderZoneRef.current) {
    renderZoneRef.current!.style.transform = `translate3d(${renderContext.columnOffset}px, ${renderContext.rowOffset}px, 0px)`
    const params = {
      top: renderContext.rowOffset,
      left: renderContext.columnOffset,
    }
    if (typeof onRenderZonePositioning === 'function') {
      onRenderZonePositioning(params)
    }
  }

  React.useEffect(() => {
    if (disableVirtualization) {
      renderZoneRef.current!.style.transform = `translate3d(0px, 0px, 0px)`
    }
  }, [disableVirtualization])

  React.useEffect(() => {
    if (containerWidth === null) {
      return
    }
    const { top, left } = scrollPosition.current!
    apiRef.current.publishEvent('scrollPositionChange', {
      top,
      left,
      leftTotalWidth,
      columnOffset:
        leftTotalWidth +
        (columnsWidthOffset?.[colRanges?.[0]?.index ?? 0] ?? 0),
      colRange: colRanges,
    })
  }, [apiRef, containerWidth, columnsWidthOffset, colRanges, leftTotalWidth])

  const handleScroll = (event: React.UIEvent) => {
    const { scrollTop, scrollLeft } = event.currentTarget
    if (scrollTop >= scrollPosition.current.top) {
      scrollDirVertical.current = 'down'
    } else {
      scrollDirVertical.current = 'up'
    }

    if (scrollLeft >= scrollPosition.current.left) {
      scrollDirHorizontal.current = 'right'
    } else {
      scrollDirHorizontal.current = 'left'
    }
    scrollPosition.current.top = scrollTop
    scrollPosition.current.left = scrollLeft
    // On iOS and macOS, negative offsets are possible when swiping past the start
    if (scrollLeft < 0 || scrollTop < 0) {
      return
    }
    const leftTotalWidth = gridColumnLeftWidthSelector(apiRef.current)

    apiRef.current.publishEvent(
      'scrollPositionChange',
      {
        top: scrollTop,
        left: scrollLeft,
        leftTotalWidth,
        columnOffset:
          leftTotalWidth +
          (columnsWidthOffset?.[colRanges?.[0]?.index ?? -1] ?? 0),
        colRange: colRanges,
      },
      event
    )
  }

  React.useEffect(() => {
    setContainerWidth(rootRef.current!.clientWidth)
  }, [rowsMeta.currentPageTotalHeight])

  const handleResize = React.useCallback<GridEventListener<'resize'>>(
    params => {
      setContainerWidth(params.width)
    },
    []
  )

  useGridApiEventHandler(apiRef, 'resize', handleResize)

  const handleContentSizeChange = useEventCallback(() => {
    const rootDimensions = apiRef.current.getRootDimensions()
    if (!rootDimensions) {
      return
    }
    setHasScrollX(rootDimensions.hasScrollX)
    setHasScrollY(rootDimensions.hasScrollY)
    const newScrollbarSize = rootDimensions.hasScrollY
      ? rootDimensions.scrollBarSize
      : 0
    if (scrollbarSize !== newScrollbarSize) {
      setScrollbarSize(newScrollbarSize)
    }
  })

  useGridApiEventHandler(
    apiRef,
    'virtualScrollerContentSizeChange',
    debounce(handleContentSizeChange, 100)
  )

  const needsHorizontalScrollbar =
    containerWidth && columnsTotalWidth > containerWidth

  const totalHeight = virtualRow.getTotalSize()

  const contentSize = React.useMemo(() => {
    // In cases where the columns exceed the available width,
    // the horizontal scrollbar should be shown even when there're no rows.
    // Keeping 1px as minimum height ensures that the scrollbar will visible if necessary.

    const height = Math.max(rowsMeta.currentPageTotalHeight, 1)
    let shouldExtendContent = false
    if (rootRef.current && height <= rootRef.current.clientHeight) {
      shouldExtendContent = true
    }
    let stickyTopHeight = '0px'
    let stickyBottomHeight = '0px'
    if (rootProps.footerPlacement === 'top') {
      stickyTopHeight = `${columnFooterHeight}px`
    } else if (rootProps.footerPlacement === 'bottom') {
      stickyBottomHeight = `${columnFooterHeight}px`
    }

    const size = {
      width: needsHorizontalScrollbar ? columnsTotalWidth : 'auto',
      height: totalHeight,
      // Todo Need to subrtact height of pinned rows top and bottom from 100% i.e calc((100%) -TopPinnedRows) - BottomPinnedRowspx)
      minHeight: shouldExtendContent
        ? `calc((100% - ${stickyTopHeight}) - ${stickyBottomHeight})`
        : 'auto',
    }
    return size
  }, [
    rowsMeta.currentPageTotalHeight,
    needsHorizontalScrollbar,
    columnsTotalWidth,
    totalHeight,
    rootProps.footerPlacement,
    columnFooterHeight,
  ])

  const remeasure = React.useCallback<
    GridEventListener<'columnWidthChange'>
  >(() => {
    virtualColumn.measure()
  }, [virtualColumn])

  React.useEffect(() => {
    apiRef.current.publishEvent('virtualScrollerContentSizeChange')
    apiRef.current.unstable_updateGridDimensionsRef()
    remeasure
  }, [apiRef, contentSize, remeasure])

  const rootStyle = {} as React.CSSProperties

  if (!needsHorizontalScrollbar) {
    rootStyle.overflowX = 'hidden'
  }
  if (rootProps.autoHeight) {
    rootStyle.overflowY = 'hidden'
  }

  if (rootProps.overscrollBehaviorX !== 'auto') {
    rootStyle.overscrollBehaviorX = rootProps.overscrollBehaviorX
  }
  if (rootProps.overscrollBehaviorY !== 'auto') {
    rootStyle.overscrollBehaviorY = rootProps.overscrollBehaviorY
  }

  const virtualiztionApi: GridVirtualizationApi = React.useMemo(
    () => ({
      getVirtualizationInstances: () => {
        return {
          virtualColumn,
          virtualRow,
        }
      },
    }),
    [virtualColumn, virtualRow]
  )

  useGridApiEventHandler(privateApiRef, 'columnWidthChange', remeasure)
  useGridApiEventHandler(privateApiRef, 'columnOrderChanged', remeasure)

  React.useEffect(() => {
    privateApiRef.current.register('public', virtualiztionApi)
  }, [virtualiztionApi, privateApiRef])

  return {
    getRootProps: ({ style = {}, ...others } = {}) => ({
      ref: handleRef,
      onScroll: handleScroll,
      style: { ...style, ...rootStyle },
      ...others,
    }),
    getContentProps: ({ style = {} } = {}) => ({
      style: { ...style, ...contentSize },
    }),
    getRenderZoneProps: () => ({
      ref: renderZoneRef,
    }),
    renderContext,
  }
}

const computeFixedRanges = (
  count: number,
  computeSize: number | ((index: number) => number)
) => {
  let start = 0
  return [...Array(count)].map((one, index) => {
    const currentSize =
      typeof computeSize === 'function' ? computeSize(index) : computeSize
    return {
      index: index,
      key: `key_${index}`,
      start: start,
      end: (start = start + currentSize),
      size: currentSize,
      measureElement: () => null,
      lane: 0,
    } as ReactVirtualItem
  })
}
